import type { UserProfile } from '@ielts-buddy/shared'
import { create } from 'zustand'
import {
  ApiError,
  clearToken,
  deleteUserMe,
  fetchMe,
  getToken,
  loginWithTelegram,
  setToken,
  syncUserFromTelegram,
} from './api.js'
import { getMiniAppInitData, isInsideTelegram } from './telegram.js'

type Status = 'idle' | 'loading' | 'authenticated' | 'error'

interface AuthState {
  status: Status
  user: UserProfile | null
  error: string | null
  bootstrap(): Promise<void>
  logout(): void
  /** Permanently deletes the user on the server, clears the token, then re-runs login (Telegram will create a fresh profile on next success). */
  deleteAccount(): Promise<void>
  /** Re-sync name / username / **photo** from Telegram WebApp `initData` (same user only). */
  syncProfileFromTelegram(): Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'idle',
  user: null,
  error: null,

  async bootstrap() {
    set({ status: 'loading', error: null })

    // Fast path: reuse existing token if still valid.
    if (getToken()) {
      try {
        const me = await fetchMe()
        set({ status: 'authenticated', user: me, error: null })
        return
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) clearToken()
        // Any other error (network, 5xx): fall through to full login.
      }
    }

    let initData: string | null = null

    if (isInsideTelegram()) {
      initData = getMiniAppInitData()
    } else if (import.meta.env.DEV) {
      // Dev-only fallback: pretend to be a Telegram user when running in a plain browser.
      // The backend must have DEV_BYPASS_TELEGRAM_AUTH=true to accept this.
      const id = import.meta.env.VITE_DEV_TG_ID ?? '999000001'
      const name = import.meta.env.VITE_DEV_TG_NAME ?? 'Dev'
      initData = `dev:${id}:${name}`
      // biome-ignore lint/suspicious/noConsole: intentional dev-only warning when not in Telegram.
      console.warn(
        '[auth] Using DEV bypass initData. Open from Telegram for real auth.',
      )
    }

    if (!initData) {
      set({
        status: 'error',
        error: 'This app must be opened from inside Telegram.',
      })
      return
    }

    try {
      const { user, token } = await loginWithTelegram(initData)
      setToken(token)
      set({ status: 'authenticated', user, error: null })
    } catch (err) {
      clearToken()
      set({ status: 'error', error: (err as Error).message })
    }
  },

  logout() {
    clearToken()
    set({ status: 'idle', user: null, error: null })
  },

  async deleteAccount() {
    await deleteUserMe()
    clearToken()
    set({ user: null, error: null })
    await get().bootstrap()
  },

  async syncProfileFromTelegram() {
    let initData = getMiniAppInitData()
    if (!initData && import.meta.env.DEV) {
      const id = import.meta.env.VITE_DEV_TG_ID ?? '999000001'
      const name = import.meta.env.VITE_DEV_TG_NAME ?? 'Dev'
      initData = `dev:${id}:${name}`
    }
    if (!initData) {
      throw new Error(
        'No Telegram initData — open the mini app from Telegram to refresh your photo.',
      )
    }
    const user = await syncUserFromTelegram(initData)
    set({ user })
  },
}))

export async function refreshMe(): Promise<UserProfile | null> {
  try {
    const me = await fetchMe()
    useAuthStore.setState({ user: me })
    return me
  } catch {
    return null
  }
}
